/**
 * Daten laden.
 *
 * Bewusst keine Abruf-Bibliothek. Was hier gebraucht wird — laden, Fehler
 * anzeigen, nach einer Änderung neu laden — sind dreißig Zeilen. Ein
 * Zwischenspeicher wäre bei einer Buchhaltung sogar schädlich: Wer einen
 * Beleg bucht und danach eine veraltete Liste sieht, bucht ihn womöglich
 * ein zweites Mal.
 */
import { useCallback, useEffect, useState } from 'react';
import { api, fehlertext } from './api';

export type Abruf<T> = {
  daten: T | null;
  laedt: boolean;
  fehler: string | null;
  /** Lädt erneut — nach jedem Vorgang, der die Daten verändert hat. */
  neuLaden: () => void;
};

export function useAbruf<T>(pfad: string | null): Abruf<T> {
  const [daten, setzeDaten] = useState<T | null>(null);
  const [laedt, setzeLaedt] = useState(pfad !== null);
  const [fehler, setzeFehler] = useState<string | null>(null);
  const [zaehler, setzeZaehler] = useState(0);

  useEffect(() => {
    if (pfad === null) {
      setzeDaten(null);
      setzeLaedt(false);
      return;
    }

    // Eine Antwort, die zu einem inzwischen überholten Aufruf gehört, darf
    // nicht mehr in den Zustand geschrieben werden: Beim schnellen Tippen
    // in einem Suchfeld kommen die Antworten nicht zwingend in der
    // Reihenfolge der Anfragen zurück, und dann stünde am Ende das Ergebnis
    // zum vorletzten Suchbegriff auf dem Bildschirm.
    let gueltig = true;
    setzeLaedt(true);
    setzeFehler(null);

    api
      .hole<T>(pfad)
      .then((ergebnis) => {
        if (!gueltig) return;
        setzeDaten(ergebnis);
        setzeLaedt(false);
      })
      .catch((f: unknown) => {
        if (!gueltig) return;
        setzeFehler(fehlertext(f));
        setzeLaedt(false);
      });

    return () => {
      gueltig = false;
    };
  }, [pfad, zaehler]);

  const neuLaden = useCallback(() => setzeZaehler((z) => z + 1), []);

  return { daten, laedt, fehler, neuLaden };
}

/**
 * Einen Vorgang auslösen, der etwas verändert.
 *
 * Hält fest, ob er läuft und woran er gescheitert ist. Solange er läuft,
 * ist der Knopf gesperrt — ein zweiter Klick auf „Buchen" würde sonst zu
 * einem zweiten Buchungsversuch führen.
 */
export function useVorgang(): {
  laeuft: boolean;
  fehler: string | null;
  setzeFehler: (text: string | null) => void;
  fuehreAus: <T>(vorgang: () => Promise<T>) => Promise<T | undefined>;
} {
  const [laeuft, setzeLaeuft] = useState(false);
  const [fehler, setzeFehler] = useState<string | null>(null);

  const fuehreAus = useCallback(
    async <T,>(vorgang: () => Promise<T>): Promise<T | undefined> => {
      setzeLaeuft(true);
      setzeFehler(null);
      try {
        return await vorgang();
      } catch (f: unknown) {
        setzeFehler(fehlertext(f));
        return undefined;
      } finally {
        setzeLaeuft(false);
      }
    },
    [],
  );

  return { laeuft, fehler, setzeFehler, fuehreAus };
}
